import { trendsForAutoPersistence } from '@src/utils/presets/transientPreset';
import { APPLY_PRESET_VIEW } from '@src/utils/presets/personalPresets';
import { takeLatest, put, debounce, select, all, call } from 'redux-saga/effects';
import { get, isEmpty } from 'lodash';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { PUSH_DRILLDOWN_FILTER } from '@store/dimensionContainer/types';
import * as filterContainerTypes from '@store/filterContainer/types';
import { selectedMetricChart, setMetrics } from '@store/metricsChartContainer/actions';
import { metricSelector, selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { getPresetKey, setPersistedData } from '@utils/persistenceUtil';
import { DEFAULT_CHART_METRICS, METRICS_TYPES_EXCLUDE_BY_FILTER } from '@utils/metrics';
import shortId from '@utils/shortId';
import { findDimensionDetailsInFilter } from '@src/utils/filter/getDimensionTypesFromFilter';
import { setSummaryWidgets } from '@store/summaryWidgets/actions';
import { isAppModule, DEFAULT_METRIC, AD_PLAYS, MARIO } from '@utils/constants';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import { preserveOrder } from '@components/common/DnD/utils';
import { SUMMARY_PANEL_TYPE } from '@components/top-widget-panel/constants';
import * as summaryWidgetsTypes from '@store/summaryWidgets/types';
import * as metricsChartContainerTypes from '@store/metricsChartContainer/types';
import * as dimensionListTypes from './dimensionList/types';
import * as trendsBaseTypes from '@store/trends/base/types';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import { initialState } from '@store/summaryWidgets/reducer';
import { FIXED_APP_FILTERS } from '@src/components/filter-panel/constants';
import {
  getFirstAppliedFilter,
  supportedMetricsSelector,
  metricsByTypesSelector,
  filterSelector,
  currentMetricsMetaSelector,
  intervalSelector,
} from './selectors';
import {
  setDimensions,
  setGlobalOrderByMetric,
  setDimensionMetrics,
} from '@store/trends/base/actions';
import * as commonTypes from './types';
import {
  applyFilter,
  persistState,
  setAppBackendType,
  updateGranularity,
  updateInterval,
} from './actions';
import { dataToPersistSelector, parseFilters, parseMetricsFn } from './persistence';
import {
  dimensionByTypesSelector,
  dimensionsListSelectorKeyValue,
} from './dimensionList/selectors';
import presetStore from '@src/globalStore/presetsStore';
import genApplyForAtomPersistence from './initStore';
import { formatPresetConfig } from '@components/common/Dashboard/UseCases/utils';
import {
  getWidgetStateForPersistence,
  initWidgetStore,
} from '@src/components/widget-panel/state/dashboard';
import { getAllowedGranularity } from '@src/components/common/GranularitySelect/utils';

function* handlePersistedState(action) {
  // persistence action is common
  // ignore if triggered from another tab
  const key = get(action, ['response', 'key'], null);

  if (!['/', '/app-experience'].includes(key)) {
    return;
  }

  const configuration = get(action, ['response', 'configuration'], {});
  yield applyState(configuration, action);
}

// Helper function to handle preset data from localStorage (unified for both flow and frustration signal)
function* handlePresetDataFromStorage(storageKey, urlParamName) {
  let presetFilter = null;
  let presetInterval: any = null;
  let presetDimensions: string[] = [];

  if (storageKey) {
    try {
      const storedData = yield call(() => localStorage.getItem(storageKey));
      if (storedData) {
        const parsedData = JSON.parse(storedData);

        // Extract filter, interval, and dimensions from stored data
        if (parsedData.filter) {
          presetFilter = parsedData.filter;
        }

        if (parsedData.interval) {
          presetInterval = parsedData.interval;
        }

        if (parsedData.selectedDimensions) {
          presetDimensions = parsedData.selectedDimensions;
        }

        // Clean up localStorage after retrieval
        yield call(() => localStorage.removeItem(storageKey));
      }
    } catch (e) {
      console.error(`Failed to parse preset data from localStorage:`, e);
    }

    // Always remove URL parameter if it exists
    yield call(() => {
      const newSearchParams = new URLSearchParams(window.location.search);
      newSearchParams.delete(urlParamName);
      const newSearch = newSearchParams.toString();
      const newPathname = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, document.title, newPathname);
    });
  }

  return { presetFilter, presetInterval, presetDimensions };
}

function* handlePersistedStateForPreset(action) {
  // persistence action for preset dashboards
  const key = get(action, ['response', 'key'], null);
  const interval = yield select(intervalSelector);
  const presetKey = getPresetKey(key);
  let presetConfig = presetStore.getState().getPreset(presetKey);

  // Check if this is a flow preset or frustration signal preset by checking the persistence key
  const isFlowPreset = key && (key.includes('/preset-flow') || /\/preset-flow:/.test(key));
  const isFrustrationSignalPreset =
    key && (key.includes('/preset-frustration-signal') || /\/preset-frustration-signal:/.test(key));
  const isEventPreset = key && (key.includes('/preset-event:') || /\/preset-event:/.test(key));

  if (isEventPreset) {
    presetConfig = presetStore.getState().eventPreset || {};
  }

  if (isEmpty(presetConfig)) {
    return;
  }

  // Get the default config from preset
  const defaultConfig = formatPresetConfig(
    presetConfig.config,
    isFlowPreset || isFrustrationSignalPreset || isEventPreset,
  );

  // Get the persisted configuration from local storage, if available
  const persistedConfiguration = get(action, ['response', 'configuration'], defaultConfig);

  // Handle flow presets, frustration signal presets, or event presets with unified workflow
  if (isFlowPreset || isFrustrationSignalPreset || isEventPreset) {
    // Get the appropriate URL parameter name
    const urlParamName = isFlowPreset
      ? 'flowPresetKey'
      : isFrustrationSignalPreset
        ? 'frustrationSignalPresetKey'
        : 'eventPresetKey';

    // Check for URL parameter
    const searchParams = yield call(() => new URLSearchParams(window.location.search));
    const storageKey = searchParams.get(urlParamName);

    // Get data from localStorage using unified helper
    const { presetFilter, presetInterval, presetDimensions } = yield* handlePresetDataFromStorage(
      storageKey,
      urlParamName,
    );

    // Create enhanced configuration with preset parameters if they exist
    let enhancedConfiguration = { ...persistedConfiguration };

    enhancedConfiguration = {
      ...enhancedConfiguration,
      filter: presetFilter || enhancedConfiguration.filter,
      interval: presetInterval || enhancedConfiguration.interval || interval,
    };

    // Apply dimensions if available
    if (presetDimensions && Array.isArray(presetDimensions) && presetDimensions.length > 0) {
      enhancedConfiguration = {
        ...enhancedConfiguration,
        dimensionKeys: Array.from(
          new Set([
            ...(Array.isArray(enhancedConfiguration.dimensionKeys)
              ? enhancedConfiguration.dimensionKeys
              : []),
            ...presetDimensions,
          ]),
        ),
      };
    }

    // Apply the enhanced configuration
    yield applyState(enhancedConfiguration, action);
    return;
  }

  // For regular presets, use the persisted configuration as normal
  yield applyState(persistedConfiguration, action);
}

function* applyState(configuration, action) {
  const { meta = {} } = action;
  const pageKey = get(action, ['response', 'key'], null);
  const supportedMetrics = yield select(supportedMetricsSelector);
  const supportedDimensions = yield select(dimensionsListSelectorKeyValue);
  const summaryWidgets = yield select(summaryWidgetsSelector);
  const parseMetrics = parseMetricsFn(supportedMetrics);

  // moved from persistence config saga to keep it generic
  const persistentFilter = get(configuration, ['filter'], null);
  if (persistentFilter) {
    const tRules = getRulesFromFilter(persistentFilter.rule, true);
    const tFilter = getFilterFromRules(tRules, true);

    const updatedRule = parseFilters(tFilter.rule);

    yield put({
      type: filterContainerTypes.RESET_DRILL_DOWN,
    });
    yield put({
      type: PUSH_DRILLDOWN_FILTER,
      name: tFilter.name,
      rule: updatedRule,
      presetFid: persistentFilter.presetFid || null,
    });
    yield setPersistedData(applyFilter(persistentFilter));
  }

  if (
    pageKey &&
    (pageKey.includes('/preset-flow') ||
      pageKey.includes('/preset-frustration-signal') ||
      pageKey.includes('/preset-event:')) &&
    configuration.interval
  ) {
    const {
      interval: { from, to, shortcut },
    } = configuration;
    yield setPersistedData(updateInterval(from, to, shortcut, 'flow-preset'));
  }

  const granularity = get(configuration, ['granularity'], null);
  if (granularity) {
    const interval = yield select(intervalSelector);
    const allowed = getAllowedGranularity(interval);
    if (allowed.includes(granularity)) {
      yield setPersistedData(updateGranularity(granularity));
    }
  }

  // better refcator required after understanding all scenarios
  const persistedSummaryWidgets = get(configuration, ['summaryWidgets'], summaryWidgets);
  switch (true) {
    case isAppModule:
      delete configuration.summaryWidgets;
      yield put(persistState({ ...configuration }));
      break;

    case !Array.isArray(persistedSummaryWidgets) &&
      persistedSummaryWidgets.type === SUMMARY_PANEL_TYPE.SPI:
      yield setPersistedData(setSummaryWidgets(persistedSummaryWidgets));
      break;

    default:
      yield setPersistedData(setSummaryWidgets(initialState));
      break;
  }

  // chart metrics
  const chartMetrics = get(configuration, ['chartMetrics'], DEFAULT_CHART_METRICS);
  yield setPersistedData(setMetrics(parseMetrics(chartMetrics)));

  // selected metric
  const selectedMetric = get(configuration, ['selectedMetric'], DEFAULT_METRIC);
  const [parsedSelectedMetric = DEFAULT_METRIC] = parseMetrics([selectedMetric]);
  yield setPersistedData(selectedMetricChart(parsedSelectedMetric));

  // order is important! dimensionMetrics needs to be set before setDimensions is called
  // new trends: dimension metrics
  const dimensionMetrics = get(configuration, ['dimensionMetrics'], null);
  if (dimensionMetrics !== null) {
    yield setPersistedData(setDimensionMetrics(parseMetrics(dimensionMetrics)));
  }

  // new trends: dimension table state
  const dimensionKeys = get(configuration, ['dimensionKeys'], null);
  if (dimensionKeys !== null) {
    let filteredDimensionKeys = dimensionKeys;
    if (Array.isArray(dimensionKeys)) {
      filteredDimensionKeys = dimensionKeys.filter((it) => supportedDimensions[it]);
    }
    yield setPersistedData(setDimensions(filteredDimensionKeys));
  }

  // This can be removed by now. but just in case if there is anyone who still had old backend this function will reset
  // their backend to mario defaults
  const appBackendType = get(configuration, ['appBackendType'], null);
  if (isAppModule && appBackendType && appBackendType !== MARIO) {
    const allTraffic = FIXED_APP_FILTERS[0];
    const tRules = getRulesFromFilter(allTraffic.rule, true);
    const tFilter = getFilterFromRules(tRules, true);
    yield all([
      put({
        type: filterContainerTypes.RESET_DRILL_DOWN,
      }),
      put({
        type: PUSH_DRILLDOWN_FILTER,
        name: tFilter.name,
        rule: tFilter.rule,
        presetFid: null,
      }),
      setPersistedData(setAppBackendType(MARIO)),
      setPersistedData(selectedMetricChart(DEFAULT_METRIC)),
      setPersistedData(setMetrics(parseMetrics(DEFAULT_CHART_METRICS))),
      setPersistedData(setGlobalOrderByMetric(DEFAULT_METRIC)),
      setPersistedData(applyFilter(allTraffic)),
    ]);
  }

  // new trends: global order by
  const globalOrderBy = get(configuration, ['globalOrderBy'], null);
  if (globalOrderBy !== null) {
    const [parsedGlobalOrderBy = parsedSelectedMetric] = parseMetrics([globalOrderBy]);
    yield setPersistedData(setGlobalOrderByMetric(parsedGlobalOrderBy));
  }

  // init zustand store for widgets
  const widgetState = get(configuration, ['widgetState'], null);
  initWidgetStore(widgetState);

  // let caller know persistence is loaded and applied
  if ('doneCallback' in meta) {
    // pass back initializer for atoms with config in closure
    const applyAtomPersistence = genApplyForAtomPersistence({ configuration, pageKey });
    meta.doneCallback(applyAtomPersistence);
  }
}

function* watchPersistedState() {
  yield takeLatest([commonTypes.UPDATE_PERSISTED_COMMON_STATE], handlePersistedState);
}

function* watchPersistedStateForPreset() {
  yield takeLatest(
    [commonTypes.UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET],
    handlePersistedStateForPreset,
  );
}

export function* handlePersistence(action) {
  // TODO: what if first action did not have skip but
  // the most recent one in debounce did?
  if (!action.skipPersistence) {
    const configuration = yield select(dataToPersistSelector);

    // check if an atom store is bound to this selector
    let atomState = {};
    if (dataToPersistSelector.getAtomState) {
      atomState = dataToPersistSelector.getAtomState();
    }

    // widget panel state
    const widgetState = getWidgetStateForPersistence();

    yield put(persistState(trendsForAutoPersistence({ ...configuration, atomState, widgetState })));
  }
}

function* handleCommonApplyFilter() {
  const filter = yield select(filterSelector);
  const selectedMetrics = yield select(metricSelector);
  const currentMetricsMeta = yield select(currentMetricsMetaSelector);
  const selectedChartMetric = yield select(selectedMetricSelector);
  const metricsByTypes = yield select(metricsByTypesSelector);
  const dimensionByTypes = yield select(dimensionByTypesSelector);
  const globalOrderBy = yield select(globalOrderBySelector);

  if (!isEmpty(dimensionByTypes)) {
    const { dimensionTypes } = findDimensionDetailsInFilter(filter, dimensionByTypes);
    let excludedMetricTypes: string[] = [];
    if (isAppModule) {
      dimensionTypes.forEach((tag) => {
        excludedMetricTypes = [...excludedMetricTypes, ...METRICS_TYPES_EXCLUDE_BY_FILTER[tag]];
      });
    }
    const uniqueExcludeMetricTypes = [...new Set(excludedMetricTypes)];
    /*
    For EI, filterdMetrics here, will be all EI metrics if applied filter has ad dimensions.
    For AI, there are no such mapping done.
    For APP, filteredMetrics is calculated based on the filter and mapping done in utils/metricList
    */
    let filteredMetrics = Object.keys(currentMetricsMeta).filter((metric) => {
      const metricType = metricsByTypes[metric];
      return uniqueExcludeMetricTypes.includes(metricType);
    });

    if (isAppModule) {
      const flowMetrics = Object.keys(currentMetricsMeta).filter(
        (metric) => currentMetricsMeta[metric].flowID,
      );
      filteredMetrics = filteredMetrics.filter((metric) => !flowMetrics.includes(metric));
      if (filteredMetrics.includes(globalOrderBy)) {
        yield put(setGlobalOrderByMetric(DEFAULT_METRIC));
      }
      if (filteredMetrics.includes(selectedChartMetric)) {
        yield put(selectedMetricChart(DEFAULT_METRIC));
        yield put(setMetrics(preserveOrder(selectedMetrics, [...selectedMetrics, DEFAULT_METRIC])));
      }
    } else {
      if (filteredMetrics.includes(globalOrderBy)) {
        yield put(setGlobalOrderByMetric(AD_PLAYS));
      }
      if (filteredMetrics.includes(selectedChartMetric)) {
        yield put(selectedMetricChart(AD_PLAYS));
        yield put(setMetrics(preserveOrder(selectedMetrics, [...selectedMetrics, AD_PLAYS])));
      }
    }
  }
}

function* watchPersistence() {
  yield debounce(
    300,
    [
      APPLY_PRESET_VIEW,
      commonTypes.APPLY_FILTER, // filter
      summaryWidgetsTypes.SET_SUMMARY_WIDGETS, // summary widgets
      metricsChartContainerTypes.SET_METRICS, // chart metrics
      metricsChartContainerTypes.SELECTED_METRIC_CHART, // selected metric
      commonTypes.UPDATE_GRANULARITY, // timeseries granularity

      // new dimension table types
      trendsBaseTypes.SWAP_SELECTED_DIMENSION,
      trendsBaseTypes.SET_SELECTED_DIMENSION,
      trendsBaseTypes.ADD_DIMENSION,
      trendsBaseTypes.REMOVE_DIMENSION,
      trendsBaseTypes.SET_DIMENSIONS,
      trendsBaseTypes.SET_GLOBAL_ORDER_BY,
      trendsBaseTypes.SET_DIMENSION_METRICS,

      // atom state
      trendsBaseTypes.PERSIST_ATOMS,

      // widget state
      trendsBaseTypes.PERSIST_WIDGETS,
    ],
    handlePersistence,
  );
}

function* watchCommonApplyFilter() {
  yield takeLatest(
    [
      commonTypes.APPLY_FILTER, // filter
      dimensionListTypes.FETCHING_SUCCESS, // dimension list
      metricsChartContainerTypes.SET_METRICS,
    ],
    handleCommonApplyFilter,
  );
}

// triggers apply filter action to reload all trends queries
function* handleRefreshTrends() {
  // trigger reload of all trends queries
  // by applying the same filter with a random key
  const appliedFilter = yield select(getFirstAppliedFilter);

  // a radom string is added to trigger refresh (_rKey)
  const refreshKey = shortId();
  yield put(applyFilter({ ...appliedFilter, _rKey: refreshKey }));
}

function* watchRefreshTrends() {
  yield takeLatest([commonTypes.REFRESH_TRENDS], handleRefreshTrends);
}

export default [
  watchPersistedState,
  watchCommonApplyFilter,
  watchPersistence,
  watchRefreshTrends,
  watchPersistedStateForPreset,
];
